import crypto from "crypto";
import {
    getCodeChallenge,
    getCodeVerifier,
    getQueryParameter,
    getQueryString,
    getRandomString,
    getUrlOrigin
} from "./silent-auth-utils";

Object.defineProperty(window, "btoa", {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    value: (str: any): string => {
        return Buffer.from(str).toString("base64");
    },
    writable: true
});

Object.defineProperty(global, "crypto", {
    value: {
        getRandomValues: (arr: Uint8Array) => crypto.randomBytes(arr.length),
        subtle: {
            digest: async (_: string, buffer: ArrayBuffer | Uint8Array): Promise<ArrayBuffer | Uint8Array> => {
                return buffer;
            }
        }
    },
    writable: true
});

describe("getQueryParameter", () => {
    it("returns the search query parameter of a given url", () => {
        expect(getQueryParameter("code", "http://test.net/?foo=bar&code=test-code")).toEqual("test-code");
    });
    it("returns null if the query parameter does not exist", () => {
        expect(getQueryParameter("code")).toBeNull();
    });
    it("should return an empty string if parameter exists but is empty", () => {
        expect(getQueryParameter("code", "http://test.net/?foo=bar&code=&bar=foo")).toEqual("");
    });
});

describe("getQueryString", () => {
    it("transforms data to query string parameters correctly", () => {
        expect(getQueryString({ foo: "bar", code: "test-code" })).toEqual("foo=bar&code=test-code");
    });
    it("should skip url parameter", () => {
        expect(getQueryString({ foo: "bar", url: "test-url" })).toEqual("foo=bar");
    });
    it("should return an empty string if data is empty", () => {
        expect(getQueryString({})).toEqual("");
        expect(getQueryString({ url: "test-url" })).toEqual("");
    });
});

describe("getUrlOrigin", () => {
    it("returns the origin for a valid url", () => {
        expect(getUrlOrigin("https://test.company.com/")).toEqual("https://test.company.com");
        expect(getUrlOrigin("https://test.company.com/path/to")).toEqual("https://test.company.com");
        expect(getUrlOrigin("https://test.company.com/?foo=bar")).toEqual("https://test.company.com");
    });
    it("returns an empty string if the url is invalid", () => {
        expect(getUrlOrigin("test.company.com")).toEqual("");
    });
});

describe("getRandomString", () => {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    let originalCrypto: any;

    beforeEach(() => {
        originalCrypto = global.crypto;
    });

    afterEach(() => {
        Object.defineProperty(global, "crypto", { value: originalCrypto });
    });

    it("should generate a cryptographically random string of specified length", () => {
        expect(getRandomString().length).toBe(45);
        expect(getRandomString(48).length).toBe(48);
    });

    it("should use msCrypto module if available", () => {
        Object.defineProperty(global, "crypto", { value: null });
        Object.defineProperty(global, "msCrypto", { value: originalCrypto });
        expect(getRandomString().length).toBe(45);
        expect(getRandomString(48).length).toBe(48);
    });

    it("should use fallback if crypto module is unsupported", () => {
        Object.defineProperty(global, "crypto", { value: null });
        expect(getRandomString().length).toBe(45);
        expect(getRandomString(48).length).toBe(48);
    });
});

describe("getCodeVerifier", () => {
    it("returns a random string that is suitable as PKCE code verifier", () => {
        expect(getCodeVerifier().length).toBeGreaterThan(43);
    });
});

describe("getCodeChallenge", () => {
    it("transforms data to query string parameters correctly", async () => {
        const codeVerifier = getCodeVerifier();
        const codeChallenge = await getCodeChallenge(codeVerifier);
        expect(codeChallenge.length).toBeGreaterThan(43);
    });
});
